import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { siteUrl } from "../components/Seo";
import { AssetClassNav, FunctionNav, SectorNav } from "../components/FilterNavs";
import ToolItem from "../components/ToolItem";
import MostUsedStrip from "../components/MostUsedStrip";
import DiscoveryStrip from "../components/DiscoveryStrip";
import SponsoredSlots from "../components/SponsoredSlots";
import Concierge from "../components/Concierge";
import { fetchCategories, fetchTools, fetchVoted, upvoteTool } from "../lib/api";
import { track } from "../lib/track";
import { slugify } from "../lib/utils-tv";
import { shareUrl } from "../lib/shareUrl";

const PRICE_RANK = { "FREE": 0, "$": 1, "$$": 2, "$$$": 3, "$$$$": 4, "$$$$$": 5, "CALL": 6, "$?": 7 };
function priceFloor(p) {
  const token = (p || "$?").split("-")[0].trim();
  return PRICE_RANK[token] ?? 7;
}
function priceCeiling(p) {
  const parts = (p || "$?").split("-");
  const token = (parts[1] || parts[0]).trim();
  return PRICE_RANK[token] ?? 7;
}

function unslugFrom(list, slug) {
  if (!slug) return "all";
  return list.find((x) => slugify(x) === slug) || "all";
}

export default function Home({ openSource = false }) {
  const [params, setParams] = useSearchParams();
  const routeParams = useParams();
  // Path-based filters (e.g. /c/equities/screeners, /f/screeners) take
  // priority over query-string ones, so we have a clean canonical URL per
  // filter combination.
  const assetSlug =
    routeParams.asset || params.get("asset") || "all";
  const fnSlug =
    routeParams.fn || routeParams.slug || params.get("function") || "all";
  // Ticker filter — `/t/:ticker` route. Normalized to uppercase
  // immediately so the rest of the page sees a canonical value.
  const activeTicker = (routeParams.ticker || params.get("ticker") || "").toUpperCase() || "all";
  // Tag filter — `/tag/:tag` route. The URL slug is lowercased; backend
  // resolves it back to a canonical tag via case-insensitive lookup.
  const activeTag = routeParams.tag || params.get("tag") || "all";
  // Sector filter — `/sector/:sector` route (GICS industry vertical).
  // Slug is lowercased; backend resolves to canonical via lookup + aliases.
  const activeSectorSlug = routeParams.sector || params.get("sector") || "all";
  // User-base filter — `/for/:userbase` route (Hedge Fund / RIA / VC / …).
  const activeUserBaseSlug = routeParams.userbase || params.get("user_base") || "all";
  // Delivery filter — ?delivery=<value> query param so it composes
  // cleanly with /c/<asset>, /f/<function>, /tag/<tag> path filters.
  // We don't introduce a /d/ path because delivery is a sub-axis users
  // toggle on top of an existing browse context, not a primary entry.
  const activeDelivery = params.get("delivery") || "all";
  // Open-source facet — driven by the dedicated /open-source route (prop)
  // or ?is_open_source=1. A property of the tool, orthogonal to delivery.
  const activeOpenSource = openSource || params.get("is_open_source") === "1";
  // Terminal facet — multi-function all-in-one platforms (Koyfin, TIKR, …).
  // ?terminals=1 toggles the filter; ?terminal_type=<flavor> sub-filters.
  const activeTerminals = params.get("terminals") === "1" || params.get("terminal_type");
  const activeTerminalType = params.get("terminal_type") || "all";
  const focusId = params.get("focus");

  const [tools, setTools] = useState([]);
  const [allTools, setAllTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [sectorVocab, setSectorVocab] = useState([]);
  const [userBaseVocab, setUserBaseVocab] = useState([]);
  const [voted, setVoted] = useState(new Set());
  const [sort, setSort] = useState("upvotes");
  const [query, setQuery] = useState(params.get("q") || "");
  const [loading, setLoading] = useState(true);

  // Debounced search tracking — fire one `search` event once the user pauses,
  // so we don't log a row per keystroke. Only meaningful queries (>=3 chars).
  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 3) return undefined;
    const t = setTimeout(() => track("search", { q, surface: "home" }), 900);
    return () => clearTimeout(t);
  }, [query]);

  // Keep the search input in sync with the URL `q` parameter so deep-links
  // from "[key] similar tools" actually populate the search box.
  useEffect(() => {
    const urlQ = params.get("q") || "";
    if (urlQ === query) return undefined;
    // Deferred so setQuery isn't a synchronous state update in the effect body.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) setQuery(urlQ);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const activeAsset = assetClasses.length
    ? unslugFrom(assetClasses, assetSlug)
    : "all";
  const activeFunction = categories.length
    ? unslugFrom(categories, fnSlug)
    : "all";
  const activeSector = sectorVocab.length
    ? unslugFrom(sectorVocab, activeSectorSlug)
    : "all";
  const activeUserBase = userBaseVocab.length
    ? unslugFrom(userBaseVocab, activeUserBaseSlug)
    : "all";

  useEffect(() => {
    // Intentional mount-only fetch. fetchCategories/fetchVoted/fetchTools are stable
    // module-level imports and do not belong in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchCategories()
      .then((r) => {
        setCategories(r.categories || []);
        setAssetClasses(r.asset_classes || []);
        setSectorVocab(r.sectors || []);
        setUserBaseVocab(r.user_bases || []);
      })
      .catch(() => {});
    fetchVoted()
      .then((ids) => setVoted(new Set(ids)))
      .catch(() => {});
    fetchTools()
      .then((t) => setAllTools(t || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Guard against the classic race: on path-based filter routes like
    // /f/ai-agents we re-fetch when activeFunction changes from "all"
    // (categories not yet loaded) to its real value once categories load.
    // The in-flight first request can resolve last and clobber the
    // filtered result. We tag each fetch and ignore stale ones. Deferred to a
    // microtask so setLoading isn't a synchronous state update in the effect.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setLoading(true);
      fetchTools({ category: activeFunction, assetClass: activeAsset, ticker: activeTicker, tag: activeTag, delivery: activeDelivery, isOpenSource: activeOpenSource, sector: activeSector, userBase: activeUserBase })
        .then((t) => {
          if (cancelled) return;
          setTools(t || []);
        })
        .catch(() => {
          if (cancelled) return;
          toast.error("Failed to load tools");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [activeFunction, activeAsset, activeTicker, activeTag, activeDelivery, activeOpenSource, activeSector, activeUserBase]);

  // Scroll to and briefly highlight a tool when ?focus=<id> is in the URL.
  // If the tool isn't in the current filter (e.g. user came from a `new` link
  // while a different asset/function filter was active), reset filters first.
  useEffect(() => {
    if (!focusId || loading) return;
    const inView = tools.some((t) => t.id === focusId);
    if (!inView) {
      // Tool exists somewhere but not in current filter — reset filters.
      const inAny = allTools.some((t) => t.id === focusId);
      if (inAny && (activeAsset !== "all" || activeFunction !== "all")) {
        setParams({ focus: focusId }, { replace: true });
      }
      return;
    }
    const el = document.getElementById(`tool-${focusId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("tv-flash");
    const t = setTimeout(() => el.classList.remove("tv-flash"), 1800);
    return () => clearTimeout(t);
  }, [focusId, tools, allTools, loading, activeAsset, activeFunction, setParams]);

  const assetCounts = useMemo(() => {
    const c = { _all: allTools.length };
    for (const t of allTools) c[t.asset_class] = (c[t.asset_class] || 0) + 1;
    return c;
  }, [allTools]);

  // Sector counts across the whole index. Most tools carry no sector, so the
  // strip only renders when at least one tagged tool exists (or a sector
  // filter is active) — avoids an all-zeros strip before any back-fill.
  const sectorCounts = useMemo(() => {
    const c = { _all: 0 };
    let any = false;
    for (const t of allTools) {
      for (const s of (t.sectors || [])) {
        c[s] = (c[s] || 0) + 1;
        c._all += 1;
        any = true;
      }
    }
    return any ? c : null;
  }, [allTools]);

  const fnCounts = useMemo(() => {
    const scope = activeAsset === "all"
      ? allTools
      : allTools.filter((t) => t.asset_class === activeAsset);
    const c = { _all: scope.length };
    for (const t of scope) {
      if (t.category) c[t.category] = (c[t.category] || 0) + 1;
      if (t.category_2 && t.category_2 !== t.category) c[t.category_2] = (c[t.category_2] || 0) + 1;
    }
    return c;
  }, [allTools, activeAsset]);

  const filteredSorted = useMemo(() => {
    let list = tools;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.key_feature.toLowerCase().includes(q) ||
          t.url.toLowerCase().includes(q),
      );
    }
    list = [...list];
    if (activeTerminals) list = list.filter((t) => t.is_terminal);
    if (activeTerminalType !== "all") {
      list = list.filter(
        (t) => Array.isArray(t.terminal_types) && t.terminal_types.includes(activeTerminalType),
      );
    }
    if (sort === "newest") list.sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || ""));
    else if (sort === "category") list.sort((a, b) => (a.category || "").localeCompare(b.category || ""));
    else if (sort === "via") {
      // Group tools sharing the same primary delivery channel together.
      // Within each channel, fall back to upvotes-desc so the best
      // claude_skill / self_host / etc. floats to the top of its group.
      // Tools without delivery sort to the bottom (empty string > any).
      list.sort((a, b) => {
        const av = (Array.isArray(a.delivery) && a.delivery[0]) || "\uffff";
        const bv = (Array.isArray(b.delivery) && b.delivery[0]) || "\uffff";
        const c = av.localeCompare(bv);
        if (c !== 0) return c;
        return (b.upvotes || 0) - (a.upvotes || 0);
      });
    }
    else if (sort === "price") list.sort((a, b) => {
      const f = priceFloor(a.pricing) - priceFloor(b.pricing);
      if (f !== 0) return f;
      const c = priceCeiling(a.pricing) - priceCeiling(b.pricing);
      if (c !== 0) return c;
      return (b.upvotes || 0) - (a.upvotes || 0);
    });
    else list.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
    return list;
  }, [tools, query, sort, activeTerminals, activeTerminalType]);

  // Terminal flavors actually present in the catalog (for the sub-filter chips
  // shown when the terminals-filtered view is active). Derived from the full
  // set so the chips show even when the current path filter has hidden tools.
  const terminalFlavors = useMemo(() => {
    const s = new Set();
    for (const t of allTools) {
      if (t.is_terminal && Array.isArray(t.terminal_types)) t.terminal_types.forEach((x) => s.add(x));
    }
    return Array.from(s);
  }, [allTools]);

  const setTerminalType = (flavor) => {
    const next = new URLSearchParams(params);
    next.set("terminals", "1");
    if (flavor === "all" || flavor === activeTerminalType) next.delete("terminal_type");
    else next.set("terminal_type", flavor);
    setParams(next);
  };

  const grouped = useMemo(() => {
    if (sort === "category") {
      const map = new Map();
      for (const t of filteredSorted) {
        if (!map.has(t.category)) map.set(t.category, []);
        map.get(t.category).push(t);
      }
      return Array.from(map.entries());
    }
    if (sort === "via") {
      // Group by primary delivery channel so the rendered list shows
      // labeled buckets ("claude_skill (3)", "self_host (8)", ...).
      const map = new Map();
      for (const t of filteredSorted) {
        const v = (Array.isArray(t.delivery) && t.delivery[0]) || "—";
        if (!map.has(v)) map.set(v, []);
        map.get(v).push(t);
      }
      return Array.from(map.entries());
    }
    return null;
  }, [filteredSorted, sort]);

  const onUpvote = async (id) => {
    try {
      const res = await upvoteTool(id);
      if (res.voted) track("upvote", { meta: { tool_id: id } });
      setTools((prev) => prev.map((t) => (t.id === id ? { ...t, upvotes: res.upvotes } : t)));
      setAllTools((prev) => prev.map((t) => (t.id === id ? { ...t, upvotes: res.upvotes } : t)));
      setVoted((prev) => {
        const n = new Set(prev);
        if (res.voted) n.add(id);
        else n.delete(id);
        return n;
      });
    } catch {
      toast.error("Could not register vote");
    }
  };

  return (
    <div data-testid="home-page">
      {(() => {
        const hasAsset = activeAsset !== "all";
        const hasFn = activeFunction !== "all";
        const hasTicker = activeTicker !== "all";
        const hasTag = activeTag !== "all";
        const hasSector = activeSector !== "all";
        const hasUserBase = activeUserBase !== "all";
        // For tag pages, derive the canonical-cased label from the tools
        // that came back (avoid hard-coding slug→canonical map on the FE).
        const tagLabel = hasTag
          ? (tools.find((t) =>
              (t.tags || []).some((tg) => slugify(tg) === activeTag),
            )?.tags || [])
              .find((tg) => slugify(tg) === activeTag) || activeTag
          : null;
        let title;
        let description;
        let path = "/";
        let image; // undefined → falls back to home OG
        if (activeOpenSource) {
          // Dedicated, shareable open-source landing page.
          title = "Open-source investing tools";
          description = "A curated index of open-source & self-hostable investing tools — backtesters, data pipelines, screeners, terminals and AI agents you can run yourself. Manually reviewed.";
          path = "/open-source";
          image = siteUrl(`/api/og/open-source.png?v=1`);
        } else if (hasTicker) {
          // Ticker pages take precedence — they're the most specific filter.
          title = `Investing tools tracking $${activeTicker}`;
          description = `Curated index of research tools, trackers, dashboards, and AI agents focused on $${activeTicker}. Manually reviewed.`;
          path = `/t/${activeTicker}`;
          image = siteUrl(`/api/og/home.png?v=4`);
        } else if (hasTag) {
          title = `${tagLabel} — investing tools`;
          description = `Curated index of ${tagLabel} tools for investors. Manually reviewed, community-upvoted.`;
          path = `/tag/${activeTag}`;
          image = siteUrl(`/api/og/home.png?v=4`);
        } else if (hasSector) {
          title = `${activeSector} sector — investing tools`;
          description = `Curated index of investing tools focused on the ${activeSector} sector — research, screeners, data and AI agents. Manually reviewed.`;
          path = `/sector/${slugify(activeSector)}`;
          image = siteUrl(`/api/og/home.png?v=4`);
        } else if (hasUserBase) {
          title = `Investing tools for ${activeUserBase.toLowerCase()}s`;
          description = `Curated index of investing tools used by ${activeUserBase.toLowerCase()}s — research, screeners, data, diligence and AI agents. Manually reviewed.`;
          path = `/for/${slugify(activeUserBase)}`;
          image = siteUrl(`/api/og/home.png?v=4`);
        } else if (hasAsset && hasFn) {
          title = `${activeFunction} for ${activeAsset}`;
          description = `Curated index of ${activeFunction.toLowerCase()} for ${activeAsset.toLowerCase()} investors. Manually reviewed.`;
          path = `/c/${slugify(activeAsset)}/${slugify(activeFunction)}`;
          image = siteUrl(`/api/og/c/${slugify(activeAsset)}/${slugify(activeFunction)}.png?v=3`);
        } else if (hasFn) {
          title = `${activeFunction} — investing tools`;
          description = `The investing tools index — ${activeFunction.toLowerCase()}. Manually reviewed, community-upvoted.`;
          path = `/f/${slugify(activeFunction)}`;
          image = siteUrl(`/api/og/f/${slugify(activeFunction)}.png?v=3`);
        } else if (hasAsset) {
          title = `${activeAsset} investing tools`;
          description = `Curated index of investing tools for ${activeAsset.toLowerCase()}. Manually reviewed.`;
          path = `/c/${slugify(activeAsset)}`;
          image = siteUrl(`/api/og/c/${slugify(activeAsset)}.png?v=3`);
        } else {
          title = null; // homepage default
          description =
            "A manually-reviewed index of investing tools — research, screeners, AI agents, data APIs, portfolio trackers and more.";
          path = "/";
        }
        const itemListLd = {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: title || "TerminalValue.io — investing tools index",
          itemListElement: filteredSorted.slice(0, 30).map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: t.slug
              ? siteUrl(`/tool/${t.slug}`)
              : t.url,
            name: t.title,
          })),
        };
        // Noindex ephemeral query-state URLs (?q=<search>, ?focus=<uuid>).
        // These are linked from <ToolItem> key-feature chips and
        // <DiscoveryStrip> tool cards, so Googlebot follows them — but
        // they're UX state, not addressable content. Canonical is "/".
        const isEphemeralQueryState = Boolean(query) || Boolean(focusId);
        // Sitewide entity graph — Organization emitted on the homepage only
        // (path "/") so AI engines & Google have a single canonical brand
        // entity to cite. (WebSite + SearchAction already ship sitewide via
        // index.html, so we don't duplicate it here.)
        const isHome = path === "/";
        const orgLd = {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "TerminalValue.io",
          url: siteUrl("/"),
          logo: siteUrl("/icons/icon-512.png"),
          description:
            "An independent, manually-reviewed index of investing tools — research, screeners, AI agents, data APIs, portfolio trackers and more.",
          sameAs: ["https://x.com/terminalvalue_1"],
        };
        const ld = isHome ? [orgLd, itemListLd] : [itemListLd];
        // FAQ schema for function/asset category pages — answers from real data.
        if (!isHome && (hasFn || hasAsset) && !hasTicker && !hasTag && !hasSector && !hasUserBase && !activeOpenSource && !query && !focusId) {
          const subj = hasFn && hasAsset
            ? `${activeFunction.toLowerCase()} for ${activeAsset.toLowerCase()} investors`
            : hasFn ? `${activeFunction.toLowerCase()} tools` : `${activeAsset.toLowerCase()} investing tools`;
          const topNames = filteredSorted.slice(0, 3).map((t) => t.title).filter(Boolean);
          const freeOne = filteredSorted.find((t) => /free/i.test(t.pricing || ""));
          const faqEntries = [
            topNames.length
              ? { q: `What are the best ${subj}?`, a: `${topNames.join(", ")} are among the most-upvoted ${subj} in the TerminalValue index.` }
              : null,
            freeOne ? { q: `Is there a free ${hasFn ? activeFunction.toLowerCase() : "tool"} option?`, a: `Yes — ${freeOne.title} (${freeOne.pricing}).` } : null,
          ].filter(Boolean);
          if (faqEntries.length) {
            ld.push({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqEntries.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            });
          }
        }
        return (
          <Seo
            title={title}
            description={description}
            path={path}
            image={image}
            jsonLd={ld}
            noindex={isEphemeralQueryState}
          />
        );
      })()}
      <Header />

      {/* Hero */}
      <section className="border-b border-black bg-white" data-testid="hero">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <h1 className="text-[18px] md:text-[20px] font-bold leading-snug">
            {">"}{" "}
            <span className="tv-hl">
              {activeTicker !== "all"
                ? `Tools tracking $${activeTicker}.`
                : activeOpenSource
                ? "Open-source investing tools."
                : activeTag !== "all"
                ? `${
                    (tools.find((t) =>
                      (t.tags || []).some((tg) => slugify(tg) === activeTag),
                    )?.tags || []).find((tg) => slugify(tg) === activeTag) ||
                    activeTag
                  } tools.`
                : activeSector !== "all"
                ? `${activeSector} sector tools.`
                : activeUserBase !== "all"
                ? `Tools for ${activeUserBase.toLowerCase()}s.`
                : "The investing tools index."}
            </span>
          </h1>
          {activeTicker !== "all" && (
            <p className="mt-2 text-[12px] text-[#666]" data-testid="ticker-subtitle">
              Showing every approved tool dedicated to{" "}
              <span className="font-bold text-black">${activeTicker}</span>.{" "}
              <Link to="/" className="tv-link">[show all tools →]</Link>
            </p>
          )}
          {activeTag !== "all" && (
            <p className="mt-2 text-[12px] text-[#666]" data-testid="tag-subtitle">
              Tools tagged with this facet. <Link to="/" className="tv-link">[show all tools →]</Link>
            </p>
          )}
          {activeOpenSource && (
            <p className="mt-2 text-[12px] text-[#666]" data-testid="open-source-subtitle">
              Source is public / self-hostable — clone, <span className="font-mono">pip install</span>, or <span className="font-mono">docker run</span> and own your stack.{" "}
              <Link to="/" className="tv-link">[show all tools →]</Link>
            </p>
          )}
          {activeSector !== "all" && (
            <p className="mt-2 text-[12px] text-[#666]" data-testid="sector-subtitle">
              Tools focused on the{" "}
              <span className="font-bold text-black">{activeSector}</span> sector.{" "}
              <Link to="/" className="tv-link">[show all tools →]</Link>
            </p>
          )}
          {activeUserBase !== "all" && (
            <p className="mt-2 text-[12px] text-[#666]" data-testid="user-base-subtitle">
              Tools used by{" "}
              <span className="font-bold text-black">{activeUserBase.toLowerCase()}s</span>.{" "}
              <Link to="/" className="tv-link">[show all tools →]</Link>
            </p>
          )}
          {/* Unique intro prose for /f and /c category pages — adds real
              on-page text + relevance signal (built from live data). */}
          {activeTicker === "all" && activeTag === "all" && activeSector === "all" &&
            activeUserBase === "all" && !activeOpenSource &&
            (activeFunction !== "all" || activeAsset !== "all") && (
            <p className="mt-2 text-[12px] text-[#666] leading-relaxed" data-testid="category-intro">
              {filteredSorted.length}{" "}
              <span className="font-bold text-black">
                {activeFunction !== "all" && activeAsset !== "all"
                  ? `${activeFunction.toLowerCase()} for ${activeAsset.toLowerCase()} investors`
                  : activeFunction !== "all"
                  ? `${activeFunction.toLowerCase()} tools`
                  : `${activeAsset.toLowerCase()} investing tools`}
              </span>{" "}
              in the index, manually reviewed and ranked by community upvotes
              {filteredSorted.slice(0, 3).map((t) => t.title).filter(Boolean).length > 0
                ? ` — including ${filteredSorted.slice(0, 3).map((t) => t.title).filter(Boolean).join(", ")}`
                : ""}
              . Compare features, pricing and who each tool is best for below.
            </p>
          )}
        </div>
      </section>

      {/* AI Concierge — only on the bare homepage (not filtered views).
          The whole point is "describe what you need; get a recommendation
          across the entire index" — once a filter is applied, the user is
          already browsing, so the chat would just be in the way. */}
      {activeAsset === "all" &&
        activeFunction === "all" &&
        activeTicker === "all" &&
        activeTag === "all" &&
        activeSector === "all" &&
        activeDelivery === "all" &&
        activeUserBase === "all" &&
        !activeOpenSource && (
          <section
            className="border-b border-black bg-white"
            data-testid="hero-concierge"
          >
            <div className="max-w-3xl mx-auto px-4 py-5">
              <Concierge compact surface="home" />
            </div>
          </section>
        )}

      <AssetClassNav assetClasses={assetClasses} active={activeAsset} counts={assetCounts} />
      <FunctionNav categories={categories} active={activeFunction} counts={fnCounts} currentAsset={activeAsset} />
      {(sectorCounts || activeSector !== "all") && (
        <SectorNav sectors={sectorVocab} active={activeSector} counts={sectorCounts || { _all: 0 }} />
      )}
      <DiscoveryStrip tools={allTools} />

      {/* Toolbar */}
      <section className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3" data-testid="toolbar">
        <input
          type="text"
          placeholder="filter the list by keyword (e.g. 'DCF', '10-K', 'macro')..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="tv-input max-w-md"
          data-testid="search-input"
        />
        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-[#888]">sort:</span>
          {/* `[via]` sort only appears once at least one tool in the
              current index has its delivery field populated. Hidden by
              default so the sort row doesn't dangle an option that
              produces a single `(no delivery set)` group across the
              entire list. Will re-appear automatically after the next
              bulk-extract run writes any delivery values. */}
          {["upvotes", "newest", "price", "category", ...(allTools.some((t) => Array.isArray(t.delivery) && t.delivery.length > 0) ? ["via"] : [])].map((s) => (
            <button
              key={s}
              onClick={() => setSort(s)}
              className={`tv-cat-pill ${sort === s ? "active" : ""}`}
              data-testid={`sort-${s}`}
            >
              [{s}]
            </button>
          ))}
        </div>
        {/* The `[⊟ terminals]` toggle used to live here next to the sort
            row; removed per product. Terminals are still reachable via each
            row's `[terminal] <type>` links (→ ?terminals=1&terminal_type=…).
            When that filtered view is active, surface the flavor sub-chips. */}
        {activeTerminals && terminalFlavors.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap text-[12px]" data-testid="terminal-flavors">
            {terminalFlavors.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setTerminalType(f)}
                className={`tv-cat-pill ${activeTerminalType === f ? "active" : ""}`}
                data-testid={`terminal-flavor-${slugify(f)}`}
              >
                {f}
              </button>
            ))}
          </div>
        )}
        {/*
         * Builder + audience filter rows intentionally hidden for now.
         * The DB fields stay in place so admin can keep tagging during
         * curation; we'll re-enable the public filter UI once enough
         * existing tools are categorized that filtering returns useful
         * results. URL params (?audience=, ?builder=) are also intentionally
         * inert until then.
         */}
        <div className="ml-auto text-[12px] text-[#888]" data-testid="result-count">
          {filteredSorted.length} {filteredSorted.length === 1 ? "tool" : "tools"}
          {activeOpenSource && <> · <span className="text-black">[open source]</span></>}
          {activeTerminals && <> · <span className="text-black">[terminals{activeTerminalType !== "all" ? `: ${activeTerminalType}` : ""}]</span></>}
          {activeAsset !== "all" && <> in <span className="text-black">[{activeAsset}]</span></>}
          {activeFunction !== "all" && <> · <span className="text-black">[{activeFunction}]</span></>}
        </div>
      </section>

      {/* Listing */}
      <main className="max-w-5xl mx-auto px-4 pb-10" data-testid="tool-list">
        <SponsoredSlots placement="home" />
        <MostUsedStrip />
        {loading ? (
          <div className="text-[13px] text-[#888] py-10" data-testid="loading">loading tools...<span className="tv-blink">_</span></div>
        ) : filteredSorted.length === 0 ? (
          <div className="text-[13px] text-[#888] py-10" data-testid="empty-state">
            no tools match. try clearing filters or{" "}
            <Link to="/submit" className="tv-link">submit one</Link>.
          </div>
        ) : grouped ? (
          grouped.map(([cat, items]) => {
            // When sort=via, the group key is a snake_case delivery value
            // (claude_skill, self_host, chatgpt_gpt, …); render the same
            // human label we use everywhere else in the UI. "—" = tools
            // with no delivery set yet.
            let heading = cat;
            if (sort === "via") {
              if (cat === "—") heading = "(no delivery set)";
              else if (cat === "chatgpt_gpt") heading = "GPT";
              else if (cat === "self_host") heading = "self-host";
              else heading = cat.replace(/_/g, " ");
            }
            return (
              <div key={cat} className="mt-4" data-testid={`group-${cat}`}>
                <h2 className="text-[13px] font-bold border-b border-black pb-1 mb-1">
                  {">"}_ {heading} <span className="text-[#888] font-normal">({items.length})</span>
                </h2>
                <ol className="list-none p-0 m-0">
                  {items.map((t, i) => (
                    <ToolItem key={t.id} index={i + 1} tool={t} voted={voted.has(t.id)} onUpvote={onUpvote} />
                  ))}
                </ol>
              </div>
            );
          })
        ) : (
          <ol className="list-none p-0 m-0">
            {filteredSorted.map((t, i) => (
              <ToolItem key={t.id} index={i + 1} tool={t} voted={voted.has(t.id)} onUpvote={onUpvote} />
            ))}
          </ol>
        )}
      </main>

      {/* Share row — placed at the bottom of the listing so it reads as
          "you've seen the list, here's how to share it". Renders on the
          bare home page AND on every active-filter view; the OG card is
          auto-generated per URL so each share renders the right card. */}
      {!loading && filteredSorted.length > 0 && (
        <section
          className="max-w-5xl mx-auto px-4 pb-10 -mt-4 border-t border-[#eee] pt-4 text-[12px] text-[#666] flex flex-wrap items-baseline gap-x-3 gap-y-1"
          data-testid="filter-share-row"
        >
          <span className="text-[#888]">
            [share {(activeAsset !== "all" || activeFunction !== "all") ? "this view" : "this index"}]
          </span>
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl(window.location.href));
                toast.success("link copied");
              } catch {
                toast.error("could not copy");
              }
            }}
            className="tv-link"
            data-testid="filter-share-copy"
            title="Copy this URL"
          >
            copy link
          </button>
          <span className="text-[#bbb]">·</span>
          <a
            href={`https://x.com/intent/post?text=${encodeURIComponent(
              activeAsset !== "all" && activeFunction !== "all"
                ? `${activeFunction} for ${activeAsset} — investing tools index`
                : activeFunction !== "all"
                ? `${activeFunction} — investing tools index`
                : activeAsset !== "all"
                ? `${activeAsset} investing tools index`
                : "TerminalValue.io — the investing tools index",
            )}&url=${encodeURIComponent(shareUrl(window.location.href))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tv-link"
            data-testid="filter-share-twitter"
          >
            x
          </a>
          <span className="text-[#bbb]">·</span>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
              shareUrl(window.location.href),
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tv-link"
            data-testid="filter-share-linkedin"
          >
            linkedin
          </a>
          <span className="text-[#bbb]">·</span>
          <a
            href={`mailto:?subject=${encodeURIComponent(
              activeAsset !== "all" && activeFunction !== "all"
                ? `${activeFunction} for ${activeAsset} — TerminalValue.io`
                : activeFunction !== "all" || activeAsset !== "all"
                ? "Investing tools — TerminalValue.io"
                : "TerminalValue.io — the investing tools index",
            )}&body=${encodeURIComponent(shareUrl(window.location.href))}`}
            className="tv-link"
            data-testid="filter-share-email"
          >
            email
          </a>
        </section>
      )}

      <Footer />
    </div>
  );
}
